import { useState, useCallback, useMemo } from 'react';
import { useRefresh } from 'react-admin';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  styled,
  TextField as MuiTextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';

import { getAdminData } from 'app/providers/auth-provider';
import { PromocodeList } from './promocodes-list';
import { AddPromocodesForm } from './add-promocode-form';
import { API_URL } from 'shared/constants/api-url';

interface PatientPromocodesProps {
  userId: string;
  buttonsView: JSX.Element;
}

const TypeChoices = [
  { id: 'amount', name: 'С номиналом' },
  { id: 'percentage', name: 'С процентом' },
];

const LimitPerUserChoices = [
  { id: 'oneuse', name: 'Одноразовый' },
  { id: 'reusable', name: 'Многоразовый' },
];

const UsedChoices = [
  { id: 'used', name: 'Использованные' },
  { id: 'unused', name: 'Не использованные' },
  { id: 'expired', name: 'Истекшие' },
];

const SearchTermChoices = [
  { id: 'code', name: 'Название промокода' },
  { id: 'adminName', name: 'Имя создателя промокода' },
];

export const Promocodes: React.FC<PatientPromocodesProps> = ({ userId, buttonsView }) => {
  const refresh = useRefresh();
  const [modalOpen, setModalOpen] = useState(false);
  const { userRole } = getAdminData();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [promocodeType, setPromocodeType] = useState('');
  const [limitPerUser, setLimitPerUser] = useState('');
  const [promocodeStatus, setPromocodeStatus] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const isSuperadmin = userRole === 'superadmin';

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleAddSuccess = useCallback(() => {
    handleCloseModal();
    refresh();
  }, [refresh]);

  const url = useMemo(() => {
    const params = new URLSearchParams({
      userId,
      limit: perPage.toString(),
      page: page.toString(),
    });

    if (searchTerm) params.append('searchTerm', searchTerm);
    if (searchValue) params.append('searchValue', searchValue);
    if (promocodeType) params.append(promocodeType, 'true');
    if (promocodeStatus) params.append(promocodeStatus, 'true');
    if (limitPerUser == 'oneuse') {
      params.append('oneuse', 'true');
    } else if (limitPerUser == 'reusable') {
      params.append('reusable', 'true');
    }

    return `${API_URL.getPromocodes}?${params.toString()}`;
  }, [userId, perPage, page, searchTerm, searchValue, promocodeType, promocodeStatus, limitPerUser]);

  const filters = useMemo(() => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
      <div style={{ display: 'flex', gap: 16 }}>
        <MuiTextField
          value={searchValue || ''}
          onChange={(e) => setSearchValue(e.target.value)}
          label="Строка для поиска"
          placeholder="Строка для поиска"
          variant="standard"
          style={{ minWidth: 200 }}
        />
        <FormControl variant="standard" style={{ minWidth: 200 }}>
          <InputLabel>Искать по:</InputLabel>
          <Select
            value={searchTerm || ''}
            onChange={(e) => setSearchTerm(e.target.value)}
            label="Искать по:"
          >
            {SearchTermChoices.map(choice => (
              <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <FormControl variant="standard" style={{ minWidth: 200 }}>
          <InputLabel>Тип</InputLabel>
          <Select
            value={promocodeType || ''}
            onChange={(e) => setPromocodeType(e.target.value)}
            label="Тип"
          >
            <MenuItem value="">Все</MenuItem>
            {TypeChoices.map(choice => (
              <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" style={{ minWidth: 200 }}>
          <InputLabel>Статус</InputLabel>
          <Select
            value={promocodeStatus || ''}
            onChange={(e) => setPromocodeStatus(e.target.value)}
            label="Статус"
          >
            <MenuItem value="">Все</MenuItem>
            {UsedChoices.map(choice => (
              <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="standard" style={{ minWidth: 200 }}>
          <InputLabel>Лимит</InputLabel>
          <Select
              value={limitPerUser || ''}
              onChange={(e) => setLimitPerUser(e.target.value)}
              label="Лимит на пользователя"
          >
            <MenuItem value="">Все</MenuItem>
            {LimitPerUserChoices.map(choice => (
                <MenuItem key={choice.id} value={choice.id}>{choice.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  ), [searchValue, searchTerm, promocodeType, promocodeStatus, limitPerUser]);

  return (
    <>
      {buttonsView}
      <AddButton
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleOpenModal}
        disabled={!isSuperadmin}
      >
        Добавить промокоды
      </AddButton>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>
          Добавление промокодов
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent>
          <AddPromocodesForm userId={userId} onSuccess={handleAddSuccess} />
        </DialogContent>
      </Dialog>

      <div style={{ marginBottom: 16 }}>
        {filters}
      </div>
      {userId && <PromocodeList 
        url={url}
        page={page}
        perPage={perPage}
        setPage={setPage}
        setPerPage={setPerPage}
      />}
    </>
  );
};

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const AddButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2]
  }
}));
